import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  Box,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1400px",
  },
  form: {
    padding: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  input: {
    flex: 1,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 1800,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontSize: "0.75rem",
    height: "24px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    color: theme.palette.grey[500],
  },
}));

interface GiftCardDetail {
  id: number;
  user_id: number;
  brand_name: string | null;
  amount: string | number;
  payment_created_date: string;
  transaction_status: string | null;
  ryft_payment_status: string | null;
  simp_id: string;
  provider_session_id: string;
  remitter_provider_id: string;
  reference: string | null;
  sprive_cancelled: number | null;
  is_refunded: number | null;
  source: string | null;
  payment_authorised_date: string | null;
  payment_execution_date: string | null;
  client_reference_id: string | null;
  purchase_status: string | null;
  rewards_amount: string | number | null;
  is_inflight: number | null;
  url: string | null;
}

interface GiftCardDetailsResponse {
  total: number;
  limit: number;
  skip: number;
  data: any[];
}

interface GiftCardDetailsHeader {
  ID: string;
  USER_ID: string;
  BRAND: string;
  AMOUNT: string;
  PAYMENT_CREATED_DATE: string;
  TRANSACTION_STATUS: string;
  RYFT_PAYMENT_STATUS: string;
  SIMP_ID: string;
  PROVIDER: string;
  REFERENCE: string;
  IS_SPRIVE_CANCELLED: string;
  REFUNDED: string;
  SOURCE: string;
  PAYMENT_AUTHORISED_DATE: string;
  PAYMENT_EXECUTION_DATE: string;
  CLIENT_REF: string;
  PURCHASE_STATUS: string;
  REWARDS_AMOUNT: string;
  IN_FLIGHT: string;
  URL: string;
}

const GiftCardTransactionDetailsPage = () => {
  const classes = useStyles();
  // const translate = useTranslate();
  const notify = useNotify();

  const [email, setEmail] = useState("");
  const [allGiftCardDetails, setAllGiftCardDetails] = useState<GiftCardDetail[]>([]);
  const [tableHeader, setTableHeader] = useState<GiftCardDetailsHeader | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);

  const fetchGiftCardDetails = async (page: number = 1, isLoadMore: boolean = false) => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setAllGiftCardDetails([]);
    }
    setError(null);

    try {
      const config = getConfigGet({ email: email.trim(), page });
      const response = await axios.get<GiftCardDetailsResponse>(ENDPOINTS.GIFT_CARD_DETAILS_PAGE, config);

      // Extract header and data from response
      if (!response.data || !response.data.data || !Array.isArray(response.data.data)) {
        throw new Error("Invalid response format: data array is missing");
      }

      const responseData = response.data.data;

      // Get header and total count from first page
      if (!isLoadMore) {
        if (responseData.length === 0) {
          throw new Error("No data found in response");
        }
        setTableHeader(responseData[0]);
        setTotalCount(response.data.total);
      }

      // Skip the header and get data rows
      const dataRows = responseData.slice(1) || [];

      if (isLoadMore) {
        setAllGiftCardDetails(prev => [...prev, ...dataRows]);
      } else {
        setAllGiftCardDetails(dataRows);
      }

      setCurrentPage(page);
      setHasSearched(true);

      // Check if there's more data to load
      const totalLoaded = isLoadMore ? (allGiftCardDetails?.length || 0) + dataRows.length : dataRows.length;
      setHasMoreData(totalLoaded < response.data.total);

      if (dataRows.length === 0 && !isLoadMore) {
        notify("No gift card details found for this email", "info");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to fetch gift card details";
      setError(errorMessage);
      notify(errorMessage, "error");
      if (!isLoadMore) {
        setAllGiftCardDetails([]);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setHasMoreData(false);
    setTableHeader(null);
    fetchGiftCardDetails(1);
  };

  const handleLoadMore = () => {
    fetchGiftCardDetails(currentPage + 1, true);
  };

  // const getStatusChip = (transactionStatus: string | null, purchaseStatus: string, isCancelled: boolean | null, isRefunded: boolean | null) => {
  //   if (isCancelled) {
  //     return <Chip label="Cancelled" color="secondary" size="small" className={classes.statusChip} />;
  //   }
  //   if (isRefunded) {
  //     return <Chip label="Refunded" color="secondary" size="small" className={classes.statusChip} />;
  //   }

  //   const status = purchaseStatus || transactionStatus || "unknown";
  //   const statusColor = status === "success" ? "primary" : status === "pending" ? "default" : status === "failed" ? "secondary" : "default";
  //   return <Chip label={status} color={statusColor} size="small" className={classes.statusChip} />;
  // };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    // Remove the 'Z' and milliseconds, then replace 'T' with space
    return dateString.replace(/\.\d{3}Z$/, '').replace('T', ' ');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  // const formatBoolean = (value: boolean | null) => {
  //   if (value === null) return "N/A";
  //   return value ? "Yes" : "No";
  // };

  const getBooleanChip = (value: boolean | null, trueColor: "primary" | "secondary" = "primary") => {
    if (value === null) {
      return <Chip label="N/A" color="default" size="small" className={classes.statusChip} />;
    }
    return (
      <Chip
        label={value ? "Yes" : "No"}
        color={value ? trueColor : "default"}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  return (
    <div className={classes.main}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Gift Card Transaction Details
          </Typography>

          <div className={classes.form}>
            <TextField
              className={classes.input}
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              size="small"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading || !email.trim()}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Search
            </Button>
          </div>

          {error && (
            <Typography color="error" style={{ marginTop: 16, padding: 16, backgroundColor: "#ffebee", borderRadius: 4 }}>
              {error}
            </Typography>
          )}

          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Loading all gift card details...
              </Typography>
            </div>
          )}

          {hasSearched && !loading && (allGiftCardDetails?.length || 0) === 0 && (
            <div className={classes.noDataContainer}>
              <Typography variant="h6">No gift card details found</Typography>
            </div>
          )}

          {(allGiftCardDetails?.length || 0) > 0 && (
            <>
              <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                Showing {allGiftCardDetails?.length || 0} of {totalCount} gift card(s)
              </Typography>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      {tableHeader ? Object.values(tableHeader).map((header, index) => (
                        <TableCell key={index} className={classes.tableCell}>{header}</TableCell>
                      )) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(allGiftCardDetails || []).map((detail) => (
                      <TableRow key={detail.id} hover>
                        {tableHeader ? Object.keys(tableHeader).map((key, index) => {
                          const renderCellContent = () => {
                            switch (key) {
                              case 'ID':
                                return detail.id;
                              case 'USER_ID':
                                return detail.user_id;
                              case 'BRAND':
                                return detail.brand_name || "N/A";
                              case 'AMOUNT':
                                return formatCurrency(parseFloat(detail.amount.toString()));
                              case 'PAYMENT_CREATED_DATE':
                                return formatDate(detail.payment_created_date || "");
                              case 'TRANSACTION_STATUS':
                                return detail.transaction_status || "N/A";
                                case 'RYFT_PAYMENT_STATUS':
                                  return detail.ryft_payment_status || "N/A";
                              case 'SIMP_ID':
                                return (
                                  <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detail.simp_id || "N/A"}
                                  </Box>
                                );
                              case 'SESSSION_ID':
                                return (
                                  <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detail.provider_session_id || "N/A"}
                                  </Box>
                                );
                              case 'PROVIDER':
                                return (
                                  <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detail.remitter_provider_id || "N/A"}
                                  </Box>
                                );
                              case 'REFERENCE':
                                return (
                                  <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detail.reference || "N/A"}
                                  </Box>
                                );
                              case 'IS_SPRIVE_CANCELLED':
                                return getBooleanChip(detail.sprive_cancelled === 1, "secondary");
                              case 'REFUNDED':
                                return getBooleanChip(detail.is_refunded === 1, "secondary");
                              case 'SOURCE':
                                return detail.source || "N/A";
                              case 'PAYMENT_AUTHORISED_DATE':
                                return formatDate(detail.payment_authorised_date || "");
                              case 'PAYMENT_EXECUTION_DATE':
                                return formatDate(detail.payment_execution_date || "");
                              case 'CLIENT_REF':
                                return (
                                  <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {detail.client_reference_id || "N/A"}
                                  </Box>
                                );
                              case 'PURCHASE_STATUS':
                                return detail.purchase_status || "N/A";
                              case 'REWARDS_AMOUNT':
                                return detail.rewards_amount ? formatCurrency(parseFloat(detail.rewards_amount.toString())) : "N/A";
                              case 'IN_FLIGHT':
                                return getBooleanChip(detail.is_inflight === 1, "primary");
                              case 'URL':
                                return detail.url ? (
                                  <Button
                                    size="small"
                                    color="primary"
                                    onClick={() => window.open(detail.url!, "_blank")}
                                  >
                                    View
                                  </Button>
                                ) : (
                                  "N/A"
                                );
                              default:
                                return "N/A";
                            }
                          };

                          return (
                            <TableCell key={index} className={classes.tableCell}>
                              {renderCellContent()}
                            </TableCell>
                          );
                        }) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More Button */}
              {hasMoreData && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}

              {/* Results Summary */}
              {hasSearched && !loading && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing {allGiftCardDetails?.length || 0} of {totalCount} transactions
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GiftCardTransactionDetailsPage;